import React, { useCallback, useMemo, useRef } from 'react';

import { type ForYouConfig } from '@atlassian/global-side-navigation/types';

import { HOME, WIKI } from '@confluence/named-routes/entry-points/routes.ts';
import { useRouteUrl } from '@confluence/route-manager/entry-points/RouteState.ts';
import {
	GO_HOME_SHORTCUT,
	GO_TO_RECENT_SHORTCUT,
	GO_TO_STARRED_SHORTCUT,
	UNIVERSAL_GO_HOME_SHORTCUT,
	UNIVERSAL_GO_TO_RECENT_SHORTCUT,
	UNIVERSAL_GO_TO_STARRED_SHORTCUT,
} from '@confluence/shortcuts/entry-points/confluence-shortcuts.ts';
import { LoadableLazy } from '@confluence/loadable';

import { useSessionData } from '@confluence/session-data/entry-points/useSessionData.ts';
import { useLivePageMode } from '@confluence/live-pages-utils/entry-points/useLivePagesStore.ts';
import { HomeOnboardingTakeoverV2HomeNudge } from '@confluence/experiment-home-onboarding-takeover-v2/entry-points/HomeOnboardingTakeoverV2HomeNudge.ts';
import { useIsEditorPage } from '@confluence/route-manager/entry-points/useIsEditorPage.ts';
import { EditorForYouSpotlight } from '@confluence/experiment-nth-user-orientation/entry-points/EditorForYouSpotlight.ts';

import { KeyboardShortcutWrapper } from '../KeyboardShortcutWrapper.tsx';
import { useRecentItems, useRecentsFilterConfig } from './useRecentItems.tsx';
import { useStarredItems } from './useStarredItems.tsx';
import { preloadStarredContentMenu } from '../../ContentMenu/preloadStarredContentMenu.ts';
import { preloadRecentContentMenu } from '../../ContentMenu/preloadRecentContentMenu.ts';

export const StarredContentMenuLoader = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-StarredContentMenu" */
				'../../ContentMenu/StarredContentMenu.tsx'
			)
		).StarredContentMenu,
});

export const RecentContentMenuLoader = LoadableLazy({
	loader: async () =>
		(
			await import(
				/* webpackChunkName: "loadable-RecentContentMenu" */
				'../../ContentMenu/RecentContentMenu.tsx'
			)
		).RecentContentMenu,
});

const isOnHomeSelector = {
	selector: (routeUrl: string | undefined) => {
		if (!routeUrl) {
			return false;
		}
		return routeUrl === HOME.toUrl() || routeUrl === WIKI.toUrl();
	},
};

export const useForYouConfig = (): ForYouConfig => {
	const { cloudId } = useSessionData();
	const [{ isEditMode: isLiveEditMode }] = useLivePageMode();
	const isOnEditRoute = useIsEditorPage();

	const isSelected = useRouteUrl(isOnHomeSelector);
	const homeUrl = HOME.toUrl();

	const isRecentOpenRef = useRef(false);
	const recentsFilterConfig = useRecentsFilterConfig();
	const recentViewAllUrl = HOME.toUrl({ view: 'recent' });
	const onRecentMouseEnter = useCallback(() => {
		if (!isRecentOpenRef.current) {
			void RecentContentMenuLoader.preload();
			void preloadRecentContentMenu(cloudId);
		}
	}, [cloudId]);

	const isStarredOpenRef = useRef(false);
	const starredViewAllUrl = HOME.toUrl({ view: 'starred' });
	const onStarredMouseEnter = useCallback(() => {
		if (!isStarredOpenRef.current) {
			void StarredContentMenuLoader.preload();
			void preloadStarredContentMenu();
		}
	}, []);

	return useMemo<ForYouConfig>(
		() => ({
			forYouItem: {
				type: 'link' as const,
				menuItemId: 'yourWork',
				url: homeUrl,
				isSelected,
				ItemWrapper: ({ children }: { children: React.ReactNode }) => {
					return (
						<KeyboardShortcutWrapper
							href={homeUrl}
							deprecatedKeyboardShortcut={
								!(isLiveEditMode || isOnEditRoute) ? GO_HOME_SHORTCUT : undefined
							}
							keyboardShortcut={UNIVERSAL_GO_HOME_SHORTCUT}
							isAppNavigationShortcut
						>
							<HomeOnboardingTakeoverV2HomeNudge>
								<EditorForYouSpotlight>{children}</EditorForYouSpotlight>
							</HomeOnboardingTakeoverV2HomeNudge>
						</KeyboardShortcutWrapper>
					);
				},
			},
			recent: {
				menuItemId: 'recent',
				useItems: useRecentItems,
				viewAllUrl: recentViewAllUrl,
				isSearchable: true,
				filterConfig: recentsFilterConfig,
				children: <RecentContentMenuLoader />,
				onOpenChange: (isOpen: boolean) => {
					isRecentOpenRef.current = isOpen;
				},
				ItemWrapper: ({ children }: { children: React.ReactNode }) => {
					return (
						<div onMouseEnter={onRecentMouseEnter} onFocus={onRecentMouseEnter}>
							<KeyboardShortcutWrapper
								deprecatedKeyboardShortcut={
									!(isLiveEditMode || isOnEditRoute) ? GO_TO_RECENT_SHORTCUT : undefined
								}
								keyboardShortcut={UNIVERSAL_GO_TO_RECENT_SHORTCUT}
								isAppNavigationShortcut
							>
								{children}
							</KeyboardShortcutWrapper>
						</div>
					);
				},
			},
			starred: {
				menuItemId: 'starred',
				useItems: useStarredItems,
				viewAllUrl: starredViewAllUrl,
				isSearchable: true,
				children: <StarredContentMenuLoader />,
				onOpenChange: (isOpen: boolean) => {
					isStarredOpenRef.current = isOpen;
				},
				ItemWrapper: ({ children }: { children: React.ReactNode }) => {
					return (
						<div onMouseEnter={onStarredMouseEnter} onFocus={onStarredMouseEnter}>
							<KeyboardShortcutWrapper
								deprecatedKeyboardShortcut={
									!(isLiveEditMode || isOnEditRoute) ? GO_TO_STARRED_SHORTCUT : undefined
								}
								keyboardShortcut={UNIVERSAL_GO_TO_STARRED_SHORTCUT}
								isAppNavigationShortcut
							>
								{children}
							</KeyboardShortcutWrapper>
						</div>
					);
				},
			},
		}),
		[
			homeUrl,
			isSelected,
			recentViewAllUrl,
			recentsFilterConfig,
			starredViewAllUrl,
			isLiveEditMode,
			isOnEditRoute,
			onRecentMouseEnter,
			onStarredMouseEnter,
		],
	);
};
